import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { format, isValid } from 'date-fns';

const { FiSearch, FiChevronLeft, FiChevronRight, FiCalendar, FiRefreshCw, FiTerminal, FiX, FiAlertCircle, FiArrowRight } = FiIcons;

const StatusBadge = ({ code }) => {
  const statusCode = parseInt(code);
  if (statusCode >= 200 && statusCode < 300) return <Badge variant="success" className="font-mono text-[10px] px-2 py-1 rounded-lg"> {statusCode} OK </Badge>;
  if (statusCode === 400 || statusCode === 401) return <Badge variant="warning" className="font-mono text-[10px] px-2 py-1 rounded-lg"> {statusCode} ERR </Badge>;
  if (statusCode === 404) return <Badge variant="neutral" className="font-mono text-[10px] px-2 py-1 rounded-lg"> {statusCode} 404 </Badge>;
  return <Badge variant="danger" className="font-mono text-[10px] px-2 py-1 rounded-lg"> {statusCode} FAIL </Badge>;
};

const ApiLogs = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const perPage = 50; // Matching PHP limit

  // Search Debounce Logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, fromDate, toDate]);

  const { data, isLoading, refetch, isFetching, isError } = useQuery({
    queryKey: ['api_logs', page, debouncedSearch, fromDate, toDate],
    queryFn: async () => {
      const response = await api.get('api_logs.php', {
        params: {
          page,
          search: debouncedSearch || undefined,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          limit: perPage
        }
      });
      return response.data;
    },
    keepPreviousData: true,
  });

  const logs = data?.data || [];
  const hasNextPage = logs.length >= perPage;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr.replace(' ', 'T'));
    return isValid(date) ? format(date, 'MMM dd, HH:mm:ss') : dateStr;
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Search and Filters Header */}
      <Card className="border-none shadow-sm bg-white overflow-visible rounded-[2rem] p-2">
        <CardContent className="p-4 flex flex-col xl:flex-row gap-4 items-stretch xl:items-center">
          {/* Search Input */}
          <div className="relative flex-1 group">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 group-focus-within:text-primary-500 transition-colors" />
            <Input 
              placeholder="Search Endpoint or Mobile..." 
              className="pl-11 h-12 bg-surface-50 border-transparent focus:bg-white focus:border-primary-200 rounded-2xl text-sm" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-surface-200 rounded-full">
                <SafeIcon icon={FiX} className="w-3 h-3 text-surface-400" />
              </button>
            )}
          </div>

          {/* Date Range Selectors */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-44">
               <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" />
               <Input 
                 type="date" 
                 className="pl-9 h-12 bg-surface-50 border-transparent rounded-2xl text-[11px] font-bold uppercase" 
                 value={fromDate} 
                 onChange={(e) => setFromDate(e.target.value)} 
               />
            </div>
            <SafeIcon icon={FiArrowRight} className="hidden sm:block text-surface-300 w-4 h-4" />
            <div className="relative w-full sm:w-44">
               <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" />
               <Input 
                 type="date" 
                 className="pl-9 h-12 bg-surface-50 border-transparent rounded-2xl text-[11px] font-bold uppercase" 
                 value={toDate} 
                 min={fromDate} // Validation: End date cannot be before start date
                 onChange={(e) => setToDate(e.target.value)} 
                 disabled={!fromDate}
               />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {(debouncedSearch || fromDate || toDate) && (
              <Button variant="ghost" onClick={clearFilters} className="h-12 px-5 text-red-500 hover:text-red-600 hover:bg-red-50 font-black text-[10px] uppercase tracking-widest rounded-2xl">
                Reset
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => refetch()} 
              disabled={isFetching} 
              className="h-12 w-12 xl:w-auto xl:px-6 rounded-2xl border-surface-200 bg-white hover:border-primary-300 transition-all shrink-0"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 xl:mr-2 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
              <span className="hidden xl:inline text-[10px] font-black uppercase tracking-widest text-surface-700">Refresh</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Logs Table */}
      <Card className="overflow-hidden border-none shadow-2xl shadow-surface-200/40 rounded-[2.5rem] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-50 bg-surface-50/50">
                <th className="px-8 py-6 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Route/Endpoint</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">User Context</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Response</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">IP Address</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-50 font-medium">
              {isLoading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}><td colSpan="5" className="px-8 py-6"><Skeleton className="h-12 w-full rounded-2xl" /></td></tr>
                ))
              ) : isError ? (
                <tr>
                  <td colSpan="5" className="py-24 text-center">
                    <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-red-100">
                      <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-400" />
                    </div>
                    <h3 className="text-red-900 font-black uppercase text-sm">Connection Fault</h3>
                    <p className="text-red-500/60 text-xs mt-1 font-bold">The system telemetry stream was interrupted</p>
                    <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-6 h-10 px-8 rounded-xl border-red-200 text-red-600 hover:bg-red-50">Reconnect</Button>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-32 text-center">
                    <div className="bg-surface-50 w-20 h-20 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 border border-surface-100">
                      <SafeIcon icon={FiTerminal} className="w-10 h-10 text-surface-200" />
                    </div>
                    <h3 className="text-surface-900 font-black uppercase text-sm">No Events Recorded</h3>
                    <p className="text-surface-400 text-[11px] mt-1 font-bold uppercase tracking-wider italic">Zero logs match the selected search criteria</p>
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-primary-50/20 transition-all duration-300 group">
                    <td className="px-8 py-6">
                      <span className="font-mono text-[10px] text-primary-700 font-black bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-100 uppercase tracking-tighter">
                        {log.endpoint}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex flex-col">
                        <span className="text-surface-900 font-black text-sm tracking-tight">{log.mobile_no || 'SYSTEM'}</span>
                        <span className="text-[10px] text-surface-400 font-bold uppercase tracking-widest mt-0.5">{log.mobile_no ? 'Consumer App' : 'Automated Task'}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <StatusBadge code={log.response_code} />
                    </td>
                    <td className="px-8 py-6">
                      <span className="font-mono text-[11px] text-surface-500 bg-surface-50 px-3 py-1 rounded-lg border border-surface-100">
                        {log.ip_address || '0.0.0.0'}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-surface-500 whitespace-nowrap text-xs font-bold uppercase tracking-tighter">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination Footer */}
        <div className="flex items-center justify-between px-8 py-8 border-t border-surface-50 bg-surface-50/20">
          <div className="flex items-center space-x-3">
            <span className="text-[10px] font-black text-surface-400 uppercase tracking-[0.2em]">Temporal Page</span>
            <div className="bg-white border-2 border-surface-200 rounded-[1rem] px-4 py-1.5 shadow-sm">
              <span className="text-sm font-black text-primary-600">{page}</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.max(1, p - 1))} 
              disabled={page === 1 || isLoading} 
              className="h-11 px-6 rounded-2xl border-surface-200 hover:bg-white text-[10px] font-black uppercase tracking-widest"
            >
              <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-2" /> Backward
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setPage(p => p + 1)} 
              disabled={!hasNextPage || isLoading} 
              className="h-11 px-6 rounded-2xl border-surface-200 hover:bg-white text-[10px] font-black uppercase tracking-widest"
            >
              Forward <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ApiLogs;